import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PLATFORM_LABEL } from '../../lib/publishing/platformRules';
import {
  formatStatsAsOf,
  METRIC_LABELS,
  type PlatformStatsRow,
  type PostMetrics,
} from '../../lib/publishing/postStats';
import type { CalendarPost, PostMedia, PostPlatform } from '../../types/publishing';
import { getPostStats } from './api';

// Publishing — per-post statistics (2026-07-20, brief Part B).
//
// Opened by clicking a post on the calendar. Serves BOTH an external (natively
// published) post and one of our own published posts — same endpoint, same shape.
//
// ═══ TWO THINGS THIS COMPONENT MUST NEVER DO ═══
//
// 1. IMPLY THE FIGURES ARE LIVE. They're an as-of-sync snapshot. The as-of line is
//    rendered unconditionally, including when the time is unknown ("Last sync time
//    unknown") — an absent caveat reads as "current".
//
// 2. RENDER A ZERO WHERE THERE IS NO MEASUREMENT. Google Business has no analytics
//    surface at all, and an unreported metric is not a measured zero. Both cases
//    render as an explicit statement or an em-dash, never 0 — telling a client
//    their post got zero engagement when we simply cannot measure it is a
//    materially different (and damaging) claim.

export interface StatsTarget {
  post: CalendarPost;
  accountName: string;
}

export function PostStatsModal({
  target,
  onClose,
}: {
  target: StatsTarget | null;
  onClose: () => void;
}) {
  const post = target?.post ?? null;
  const stats = useQuery({
    queryKey: ['post-stats', post?.id, post?.platforms],
    queryFn: () => getPostStats(post!.id, post!.platforms),
    enabled: !!post,
    staleTime: 60_000,
  });

  if (!target || !post) return null;
  const permalink = 'permalink' in post ? ((post as { permalink?: string | null }).permalink ?? null) : null;
  const data = stats.data;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Post performance
            {post.origin === 'external' && (
              <Badge variant="neutral" title="Published natively on the page, not through Kyck">
                Posted natively
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {target.accountName}
            {post.scheduledFor && ` · ${new Date(post.scheduledFor).toLocaleString()}`}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto">
          <PostSummaryBlock post={post} permalink={permalink ?? data?.stats?.permalink ?? null} />

          {stats.isLoading && <CenteredSpinner />}

          {!stats.isLoading && data?.unavailable === 'gbp-unsupported' && (
            <UnavailableNote
              title="Analytics aren't available for Google Business posts"
              body="Google doesn't expose post-level performance data for Business Profile posts, so there are no figures to show. This is a platform limitation — it does not mean the post had no engagement."
            />
          )}

          {!stats.isLoading && data?.unavailable === 'not-found' && (
            <UnavailableNote
              title="No statistics yet"
              body="This post hasn't been picked up by the analytics sync yet. Figures usually appear within a day of publishing."
            />
          )}

          {!stats.isLoading && (data?.unavailable === 'error' || stats.isError) && (
            <UnavailableNote
              title="Couldn't load statistics"
              body={data?.message ?? 'The analytics lookup failed. Try again shortly.'}
              tone="danger"
            />
          )}

          {data?.stats && (
            <>
              <MetricGrid metrics={data.stats.metrics} />
              <PlatformBreakdown rows={data.stats.platforms} />
              {/* The as-of line. Rendered whenever figures are shown — never
                  conditionally, or its absence would imply live data. */}
              <p className="text-xs text-slate-500">
                Statistics are a snapshot from the last sync, not live —{' '}
                {formatStatsAsOf(data.stats.lastUpdated ?? data.stats.lastSync, new Date())}.
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PostSummaryBlock({
  post,
  permalink,
}: {
  post: CalendarPost;
  permalink: string | null;
}) {
  const media = post.media[0];
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      {/* Media is the centrepiece — big, object-contain so portrait AND landscape
          fit uncropped, video playable inline. Caption + link sit BENEATH it. */}
      {media && <StatsMedia media={media} />}
      <div className="p-3">
        <p className="whitespace-pre-wrap text-sm text-slate-700">
          {post.content || '(no caption)'}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {post.platforms.map((p) => (
            <span key={p} className="flex items-center gap-1 text-xs text-slate-500">
              <ChannelIcon channel={p} className="h-3.5 w-3.5" />
              {PLATFORM_LABEL[p]}
            </span>
          ))}
          {permalink && (
            <a
              href={permalink}
              target="_blank"
              rel="noreferrer noopener"
              className="ml-auto flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
            >
              View on {viewOnLabel(post.platforms)}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Prominent media area for the stats modal — full width, letterboxed, uncropped. */
function StatsMedia({ media }: { media: PostMedia }) {
  if (media.type === 'video') {
    return (
      <div className="flex max-h-[380px] w-full items-center justify-center bg-black">
        <video
          src={media.url}
          poster={media.thumbnailUrl ?? undefined}
          controls
          playsInline
          className="max-h-[380px] w-full object-contain"
        />
      </div>
    );
  }
  return (
    <div className="flex max-h-[380px] min-h-[120px] w-full items-center justify-center bg-slate-100">
      <img
        src={media.url}
        alt={media.altText || 'post media'}
        className="max-h-[380px] w-full object-contain"
        // Fall back to the thumbnail once (GBP rows carry url only; FB/IG carry
        // both), then drop rather than leave a broken-image frame.
        onError={(e) => {
          const el = e.currentTarget;
          if (media.thumbnailUrl && el.src !== media.thumbnailUrl) {
            el.src = media.thumbnailUrl;
          } else {
            el.style.display = 'none';
          }
        }}
      />
    </div>
  );
}

function viewOnLabel(platforms: PostPlatform[]): string {
  const first = platforms[0];
  return first ? PLATFORM_LABEL[first] : 'platform';
}

function MetricGrid({ metrics }: { metrics: PostMetrics | null }) {
  if (!metrics) {
    return (
      <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">
        No metrics reported for this post yet.
      </p>
    );
  }
  // Only metrics the platform actually reported. A metric that came back absent is
  // omitted rather than shown as 0 — see the header note.
  const shown = METRIC_LABELS.filter(({ key }) => metrics[key] !== null);
  if (shown.length === 0) {
    return (
      <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">
        No metrics reported for this post yet.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {shown.map(({ key, label }) => (
        <div key={key} className="rounded-lg border border-slate-200 px-3 py-2">
          <div className="text-lg font-semibold tabular-nums text-slate-900">
            {metrics[key]!.toLocaleString()}
          </div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      ))}
      {metrics.engagementRate !== null && (
        <div className="rounded-lg border border-slate-200 px-3 py-2">
          <div className="text-lg font-semibold tabular-nums text-slate-900">
            {metrics.engagementRate.toFixed(1)}%
          </div>
          <div className="text-xs text-slate-500">Engagement rate</div>
        </div>
      )}
    </div>
  );
}

/** Per-destination breakdown — a multi-destination post performs differently per network. */
function PlatformBreakdown({ rows }: { rows: PlatformStatsRow[] }) {
  if (rows.length <= 1) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-700">By destination</h4>
      {rows.map((row) => (
        <div key={row.platform} className="rounded-lg border border-slate-200 px-3 py-2">
          <div className="flex items-center gap-2">
            <ChannelIcon channel={row.platform} className="h-4 w-4" />
            <span className="text-sm font-medium text-slate-800">
              {PLATFORM_LABEL[row.platform]}
            </span>
            {!row.measurable && (
              <Badge variant="neutral" className="ml-auto">
                No analytics available
              </Badge>
            )}
          </div>
          {row.measurable && (
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
              {row.metrics ? (
                METRIC_LABELS.filter(({ key }) => row.metrics![key] !== null).map(
                  ({ key, label }) => (
                    <span key={key}>
                      <span className="font-medium tabular-nums text-slate-900">
                        {row.metrics![key]!.toLocaleString()}
                      </span>{' '}
                      {label.toLowerCase()}
                    </span>
                  ),
                )
              ) : (
                <span className="text-slate-400">Not reported yet</span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function UnavailableNote({
  title,
  body,
  tone = 'neutral',
}: {
  title: string;
  body: string;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <div
      className={
        tone === 'danger'
          ? 'rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5'
          : 'rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5'
      }
    >
      <p
        className={
          tone === 'danger'
            ? 'text-sm font-medium text-rose-800'
            : 'text-sm font-medium text-slate-800'
        }
      >
        {title}
      </p>
      <p className={tone === 'danger' ? 'mt-1 text-xs text-rose-700' : 'mt-1 text-xs text-slate-600'}>
        {body}
      </p>
    </div>
  );
}
